import { useMemo, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Flame, CheckCircle2, XCircle, IndianRupee, Users, AlertTriangle } from 'lucide-react';

const inr = n => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const Kpis = ({ t }) => {
  const cards = [
    { k: 'hot', label: 'Hot leads transferred', value: t.hot, icon: Flame, tint: 'bg-orange-50 text-orange-600' },
    { k: 'qualified', label: 'Qualified', value: t.qualified, icon: CheckCircle2, tint: 'bg-emerald-50 text-emerald-600' },
    { k: 'excluded', label: 'Left out', value: t.excluded, icon: XCircle, tint: 'bg-rose-50 text-rose-600' },
    { k: 'users', label: 'Users earning', value: t.users, icon: Users, tint: 'bg-blue-50 text-blue-600' },
    { k: 'amount', label: 'Total payout', value: inr(t.amount), icon: IndianRupee, tint: 'bg-slate-100 text-slate-700' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="hlt-kpis">
      {cards.map(c => (
        <Card key={c.k} className="p-4" data-testid={`hlt-kpi-${c.k}`}>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${c.tint}`}><c.icon className="w-4 h-4" /></div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{c.label}</div>
          <div className="text-xl font-bold mt-0.5 text-slate-900">{c.value}</div>
        </Card>
      ))}
    </div>
  );
};

const People = ({ people, sel, onSel }) => (
  <Card className="p-4" data-testid="hlt-people-table">
    <h3 className="text-sm font-bold text-slate-900">User-wise payout</h3>
    <p className="text-[11px] text-slate-500 mb-3">Click a row to see that person's leads.</p>
    <table className="w-full text-xs">
      <thead className="text-[10px] uppercase tracking-wider text-slate-400"><tr>
        <th className="text-left py-1">Emp ID</th><th className="text-left">Employee</th><th className="text-left">CRM user</th>
        <th className="text-right">Hot</th><th className="text-right">Qualified</th><th className="text-right">Left out</th><th className="text-right">Payout</th></tr></thead>
      <tbody>
        {people.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-8">No Hot lead transfers for this month.</td></tr>}
        {people.map(p => (
          <tr key={p.emp_id} onClick={() => onSel(sel === p.emp_id ? '' : p.emp_id)}
            className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${sel === p.emp_id ? 'bg-blue-50/60' : ''}`} data-testid={`hlt-person-${p.emp_id}`}>
            <td className="py-2 font-mono">{p.emp_id}</td>
            <td>{p.name || <span className="text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Not linked</span>}</td>
            <td className="text-slate-500">{p.crm_user_name || '—'}</td>
            <td className="text-right">{p.hot}</td><td className="text-right text-emerald-700">{p.qualified}</td>
            <td className="text-right text-rose-600">{p.excluded}</td><td className="text-right font-bold">{inr(p.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </Card>
);

const Leads = ({ leads, sel, show, setShow }) => (
  <Card className="p-4" data-testid="hlt-leads-table">
    <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
      <h3 className="text-sm font-bold text-slate-900">Leads {sel && <span className="font-mono text-slate-500">· {sel}</span>}</h3>
      <div className="flex gap-1 text-xs">
        {[['all', 'All'], ['yes', 'Qualified'], ['no', 'Left out']].map(([k, l]) => (
          <button key={k} onClick={() => setShow(k)} className={`px-2 py-1 rounded ${show === k ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`} data-testid={`hlt-filter-${k}`}>{l}</button>
        ))}
      </div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-wider text-slate-400"><tr>
          <th className="text-left py-1">Hot date</th><th className="text-left">Lead</th><th className="text-left">Emp ID</th><th className="text-left">Lead owner</th>
          <th className="text-left">Before Hot</th><th className="text-left">Sales User Status</th><th className="text-right">Amount</th><th className="text-left pl-3">Why left out</th></tr></thead>
        <tbody>
          {leads.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8">No leads.</td></tr>}
          {leads.map(x => (
            <tr key={x.id} className="border-t border-slate-100" data-testid={`hlt-lead-${x.id}`}>
              <td className="py-1.5 whitespace-nowrap">{x.hot_date}</td>
              <td><a href={`https://crm.zoho.com/crm/tab/Leads/${x.id}`} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">{x.name || x.id}</a></td>
              <td className="font-mono">{x.emp_id}</td><td>{x.owner}</td>
              <td>{x.prior_status || '—'}</td><td>{x.sales_status || '—'}</td>
              <td className={`text-right font-semibold ${x.qualifies ? 'text-emerald-700' : 'text-slate-300'}`}>{inr(x.amount)}</td>
              <td className="pl-3 text-rose-600">{x.reasons.join('; ')}{x.qualifies && !x.linked && <span className="text-amber-700">Paid — Emp ID not linked yet</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

export const HltReport = ({ data, onSettings }) => {
  const [sel, setSel] = useState('');
  const [show, setShow] = useState('all');
  const leads = useMemo(() => data.leads.filter(x => (!sel || x.emp_id === sel) && (show === 'all' || (show === 'yes') === x.qualifies)), [data.leads, sel, show]);
  return (
    <div className="space-y-4">
      <Kpis t={data.totals} />
      {data.totals.unlinked.length > 0 && (
        <button onClick={onSettings} className="w-full text-left text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2 flex items-center gap-2" data-testid="hlt-unlinked-banner">
          <AlertTriangle className="w-3.5 h-3.5" />{data.totals.unlinked.length} Emp ID(s) not linked to an employee: {data.totals.unlinked.join(', ')} —
          {data.settings?.pay_unlinked ? ' their leads are still paid.' : ' their leads are not paid until linked.'} Open Settings to link them.</button>
      )}
      <People people={data.people} sel={sel} onSel={setSel} />
      <Leads leads={leads} sel={sel} show={show} setShow={setShow} />
    </div>
  );
};
